import React from 'react'
import type {
  SignupStep1FieldName,
  SignupStep1Values,
  SignupStep3FieldName,
} from '@data/signup-form'

/**
 * In-memory field values for the signup funnel, so Back (the in-page link or
 * the browser button) returns to a step with its answers still filled in.
 *
 * Mounted in `wrapRootElement`, which survives Gatsby route changes but not a
 * reload or a new tab — so a fresh load still starts blank, the rule the
 * funnel has always followed. Memory, not sessionStorage, on purpose: Step 3
 * holds the visitor's name and email, and nothing here is ever written where
 * another script on the page could read it back.
 *
 * Separate from the sessionStorage draft (`src/util/signup-storage.ts`), which
 * still owns branching and route guards and is only written on submit. This
 * only ever prefills.
 *
 * Consent and the honeypot are deliberately not remembered: the terms box is
 * ticked on the submit that relies on it.
 */

/**
 * The Step 2 form as typed, before it becomes a `SignupStep2Values` draft —
 * `detail` here is the dropdown's value, not the name of the branch field.
 */
export interface SignupStep2FormValues {
  detail: string
  companyName: string
}

export type SignupStep3Values = Record<SignupStep3FieldName, string>

interface SignupFormState {
  step1: SignupStep1Values
  step2: SignupStep2FormValues
  step3: SignupStep3Values
}

interface SignupFormContextData extends SignupFormState {
  setStep1Field: (name: SignupStep1FieldName, value: string) => void
  setStep2Field: (name: keyof SignupStep2FormValues, value: string) => void
  setStep3Field: (name: SignupStep3FieldName, value: string) => void
  resetSignupForm: () => void
}

const EMPTY_STATE: SignupFormState = {
  step1: { useCase: '', industry: '', accountType: '' },
  step2: { detail: '', companyName: '' },
  step3: { firstName: '', lastName: '', email: '' },
}

const SignupFormContext = React.createContext<SignupFormContextData | null>(
  null,
)

export const SignupFormProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [state, setState] = React.useState<SignupFormState>(EMPTY_STATE)

  const setStep1Field = React.useCallback(
    (name: SignupStep1FieldName, value: string) =>
      setState((current) => ({
        ...current,
        step1: { ...current.step1, [name]: value },
        // The Step 2 dropdown is chosen by Account Type, so its answer belongs
        // to the old branch once that changes. Company Name is asked on every
        // branch and survives.
        step2:
          name === 'accountType' && value !== current.step1.accountType
            ? { ...current.step2, detail: '' }
            : current.step2,
      })),
    [],
  )

  const setStep2Field = React.useCallback(
    (name: keyof SignupStep2FormValues, value: string) =>
      setState((current) => ({
        ...current,
        step2: { ...current.step2, [name]: value },
      })),
    [],
  )

  const setStep3Field = React.useCallback(
    (name: SignupStep3FieldName, value: string) =>
      setState((current) => ({
        ...current,
        step3: { ...current.step3, [name]: value },
      })),
    [],
  )

  const resetSignupForm = React.useCallback(() => setState(EMPTY_STATE), [])

  const value = React.useMemo(
    () => ({
      ...state,
      setStep1Field,
      setStep2Field,
      setStep3Field,
      resetSignupForm,
    }),
    [state, setStep1Field, setStep2Field, setStep3Field, resetSignupForm],
  )

  return (
    <SignupFormContext.Provider value={value}>
      {children}
    </SignupFormContext.Provider>
  )
}

export const useSignupForm = (): SignupFormContextData => {
  const context = React.useContext(SignupFormContext)
  if (!context) {
    throw new Error(
      'useSignupForm must be used inside SignupFormProvider (wrap-root-element.js)',
    )
  }
  return context
}
